/**
 * AnalyticsTracker — a tiny first-party "Google-Analytics" agent.
 *
 * Mounted once inside <BrowserRouter>. For every route the visitor lands on it
 * opens a "view" (one row in AnalyticsEvents, keyed by a client-generated UUID)
 * and keeps it updated with the *active* time on page, scroll depth and click
 * count. Heartbeats every 15s feed the admin "live users" panel; a sendBeacon
 * on unload captures the final dwell time even when the tab closes.
 *
 * Design notes:
 *  - Only counts time while the tab is actually visible (no idle inflation).
 *  - Sends the bearer token by hand (sendBeacon/fetch bypass the axios
 *    interceptor) so logged-in views get attributed to the user.
 *  - Skips /admin/* so an admin poking around their own dashboard doesn't
 *    pollute the storefront numbers.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const COLLECT_URL = (process.env.REACT_APP_API_URL || '/api') + '/analytics/collect';
const HEARTBEAT_MS = 15000;

const uuid = () => (
  (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      })
);

// Persistent (this browser, across visits) + per-tab session identifiers.
function getVisitorId() {
  try {
    let v = localStorage.getItem('tt_vid');
    if (!v) { v = uuid(); localStorage.setItem('tt_vid', v); }
    return v;
  } catch { return uuid(); }
}
function getSessionId() {
  try {
    let v = sessionStorage.getItem('tt_sid');
    if (!v) { v = uuid(); sessionStorage.setItem('tt_sid', v); }
    return v;
  } catch { return uuid(); }
}

// Sticky campaign/UTM attribution. The first link a visitor lands on carries
// the campaign (?c=mahalo or ?utm_campaign=mahalo); we persist it for the whole
// session so every later pageview — including the order — stays attributed to
// the marketing effort that brought them in, not just the first page.
function getAttribution() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = {
    // ?c= is the short campaign codeword; utm_campaign is the long-form equivalent.
    campaign: (params.get('c') || params.get('utm_campaign') || '').trim().toLowerCase(),
    source:   (params.get('utm_source') || '').trim(),
    medium:   (params.get('utm_medium') || '').trim(),
  };
  // Anything present on the URL wins and gets persisted for the rest of the visit.
  if (fromUrl.campaign || fromUrl.source || fromUrl.medium) {
    try { sessionStorage.setItem('tt_attribution', JSON.stringify(fromUrl)); } catch {}
    return fromUrl;
  }
  try {
    const saved = JSON.parse(sessionStorage.getItem('tt_attribution') || 'null');
    if (saved) return saved;
  } catch {}
  return fromUrl; // all empty
}

export default function AnalyticsTracker() {
  const location = useLocation();
  const view = useRef(null);     // current page-view accumulator
  const prevPath = useRef(null); // for in-app referrer chaining

  // ── low-level sender ────────────────────────────────────────────────────
  function post(payload, useBeacon) {
    try {
      const body = JSON.stringify(payload);
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(COLLECT_URL, new Blob([body], { type: 'application/json' }));
        return;
      }
      const headers = { 'Content-Type': 'application/json' };
      let token = null;
      try { token = localStorage.getItem('accessToken'); } catch {}
      if (token) headers.Authorization = `Bearer ${token}`;
      fetch(COLLECT_URL, { method: 'POST', headers, body, keepalive: true, credentials: 'include' })
        .catch(() => {});
    } catch {}
  }

  // Roll the active-time clock forward into the accumulator.
  function accrue(v) {
    if (v && v.activeSince != null) {
      v.activeMs += Date.now() - v.activeSince;
      v.activeSince = Date.now();
    }
  }

  function snapshot(v, type) {
    accrue(v);
    return {
      type,
      id: v.id,
      visitorId: v.visitorId,
      sessionId: v.sessionId,
      path: v.path,
      title: v.title,
      referrer: v.referrer,
      isEntry: v.isEntry,
      durationMs: v.activeMs,
      maxScrollPct: v.maxScroll,
      clicks: v.clicks,
      screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language || (navigator.languages && navigator.languages[0]) || '',
      timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; } })(),
      utmSource: v.utm.source,
      utmMedium: v.utm.medium,
      utmCampaign: v.utm.campaign,
    };
  }

  // ── start / finalize a page view ────────────────────────────────────────
  function startView(path) {
    const attribution = getAttribution();
    const isEntry = (() => {
      try {
        const done = sessionStorage.getItem('tt_entry_done');
        if (!done) { sessionStorage.setItem('tt_entry_done', '1'); return true; }
      } catch {}
      return false;
    })();

    view.current = {
      id: uuid(),
      visitorId: getVisitorId(),
      sessionId: getSessionId(),
      path,
      title: document.title || '',
      referrer: prevPath.current || document.referrer || '',
      isEntry,
      activeMs: 0,
      activeSince: document.visibilityState === 'visible' ? Date.now() : null,
      maxScroll: 0,
      clicks: 0,
      utm: {
        source: attribution.source || '',
        medium: attribution.medium || '',
        campaign: attribution.campaign || '',
      },
    };
    post(snapshot(view.current, 'view'), false);
  }

  function endView(useBeacon) {
    const v = view.current;
    if (!v) return;
    post(snapshot(v, 'end'), useBeacon);
    view.current = null;
  }

  // ── route change: close the old view, open a new one ────────────────────
  useEffect(() => {
    const path = location.pathname + location.search;

    if (path.startsWith('/admin')) {
      // Don't track admin's own browsing — but still close any open storefront
      // view so its dwell time is saved.
      endView(false);
      prevPath.current = path;
      return;
    }

    endView(false);
    startView(path);
    prevPath.current = path;

    // Heartbeat keeps "live" fresh and persists growing dwell time.
    const hb = setInterval(() => {
      if (view.current) post(snapshot(view.current, 'heartbeat'), false);
    }, HEARTBEAT_MS);

    return () => clearInterval(hb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // ── page-level listeners (mounted once) ─────────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      const v = view.current;
      if (!v) return;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const pct = scrollable > 0 ? Math.round((window.scrollY / scrollable) * 100) : 100;
      if (pct > v.maxScroll) v.maxScroll = Math.min(100, pct);
    };
    const onClick = () => { if (view.current) view.current.clicks += 1; };
    const onVisibility = () => {
      const v = view.current;
      if (!v) return;
      if (document.visibilityState === 'hidden') {
        accrue(v);
        v.activeSince = null;
        post(snapshot(v, 'heartbeat'), true); // flush on tab hide
      } else {
        v.activeSince = Date.now();
      }
    };
    const onPageHide = () => endView(true);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('click', onClick, true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('click', onClick, true);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
